import {
  DevelopmentPathChangeSourceDto,
  DevelopmentPathChangeTypeDto,
  DevelopmentPathStepFieldDto,
  ResourceTypeDto,
  TaskStatusDto,
} from '@/api/generated';

export type MyProfileTranslation = {
  activities: {
    count: string;
    documents: {
      adminSectionTitle: string;
      adminSource: string;
      aiContext: string;
      aiContextTooltip: string;
      deleteDialog: { cancel: string; confirm: string; message: string; title: string };
      dropzoneHintPrefix: string;
      dropzoneLabel: string;
      dropzoneMaxSize: string;
      embeddingStatus: {
        available: string;
        enableHint: string;
        failed: string;
        noContent: string;
        processing: string;
        unsupported: string;
        willBeAvailable: string;
      };
      reembed: string;
      title: string;
    };
    empty: string;
    fields: {
      developmentProgram: string;
      editYear: string;
    };
    noAccess: string;
    showing: string;
    showMore: string;
    status: {
      completed: string;
      inProgress: string;
      planned: string;
    };
    tabs: {
      active: string;
      archived: string;
    };
  };
  basicInfo: {
    actions: {
      downloadFlashcard: string;
    };
    elevated: {
      absenceStatus: string;
      activeEmployee: string;
      additionalInfo: string;
      age: string;
      aiSummaryDisclaimer: string;
      aiSummaryFailed: string;
      aiSummaryGenerating: string;
      aiSummaryRetry: string;
      aiSummaryTakingLonger: string;
      aiSummaryTitle: string;
      annualEvaluation: string;
      careerPath: string;
      company: string;
      countryMobility: string;
      developmentContext: string;
      education: string;
      englishLevel: string;
      evaluationType: string;
      expatStatus: string;
      feedback: {
        form: {
          cancelButton: string;
          description: string;
          label: string;
          sendNotProvidedButton: string;
          sendProvidedButton: string;
          submitNotFeedbackButton: string;
        };
        noReason: string;
        notProvided: string;
        provided: string;
      };
      finalGrade: string;
      functionalArea: string;
      grade: string;
      internationalMobility: string;
      latestProfileData: string;
      leadershipPotential: string;
      leavingRisk: string;
      managementTrainee: string;
      mdrResult: string;
      no: string;
      noComment: string;
      noEvaluationData: string;
      noMdrData: string;
      personalLevel: string;
      potential: string;
      profileDetails: string;
      profileSignals: string;
      provided: string;
      qualitativeGrade: string;
      quantitativeGrade: string;
      reviewComment: string;
      sopAnalysis: string;
      sopResult: string;
      yes: string;
    };
    employeeSupervisorsLabel: string;
    fields: {
      department: string;
      hrBusinessPartner: string;
      position: string;
      region: string;
      sapNumber: string;
      tenureJmp: string;
      tenurePosition: string;
    };
    roles: {
      employeeSelf: string;
      hrBusinessPartner: string;
      p1Manager: string;
      p2Manager: string;
      self: string;
    };
    supervisorsLabel: string;
    title: string;
  };
  common: {
    noData: string;
  };
  dashboard: {
    activities: {
      continue: string;
      empty: string;
      more: string;
      open: string;
      title: string;
    };
    currentSteps: {
      daysAgo: string;
      empty: string;
      moreTasks: string;
      title: string;
    };
    directionLabel: string;
    employeeDirectionLabel: string;
    emptyPlan: {
      action: string;
      description: string;
      title: string;
    };
    goToDevelopment: string;
    goToEmployeeDevelopment: string;
    noDirection: string;
    roleView: {
      activitiesInProgress: string;
      completedSteps: string;
      emptyPlan: {
        description: string;
        title: string;
      };
      giveFeedback: string;
      goToPlan: string;
      noOverdue: string;
      overdueDays: string;
      overdueSteps: string;
      planDirection: string;
      planProgress: string;
    };
    stats: {
      activitiesInProgress: string;
      overdueTasks: string;
      stepsCompleted: string;
    };
    title: string;
  };
  development: {
    aiAssistant: {
      aiNotice: {
        intro: string;
        points: { errors: string; otherPeopleData: string; security: string };
        rememberLabel: string;
      };
      casualGreeting: string;
      casualMode: string;
      casualModeHeader: string;
      casualSuggestions: {
        conflictResolution: string;
        feedback: string;
        keyCompetencies: string;
        matchProjects: string;
        taskDelegation: string;
      };
      employeeModeHeader: string;
      noActivePlan: string;
      noActivePlanDescription: string;
      planGreeting: string;
      planMode: string;
      planModeHeader: string;
      planSuggestions: {
        addStep: string;
        nextStep: string;
        summarize: string;
        update: string;
      };
      selectMode: string;
      title: string;
    };
    campaignInactive: {
      description: string;
      employeeDescription: string;
      employeeTitle: string;
      title: string;
    };
    certificationResults: {
      description: string;
      link: string;
    };
    employeeTitle: string;
    emptyPlan: {
      chatReadyDescription: string;
      create: string;
      creating: string;
      description: string;
      direction: string;
      directionFromCampaign: string;
      initialize: string;
      noPermission: string;
      submit: string;
      supervisorDescription: string;
      title: string;
      validFrom: string;
      validTo: string;
    };
    goalBar: {
      approver: string;
      cancel: string;
      cycle: string;
      directionFromCampaign: string;
      directionLabel: string;
      save: string;
      statusChange: {
        approval: {
          approve: string;
          awaitingMessage: string;
          cancel: string;
          commentLabel: string;
          commentPlaceholder: string;
          requestChanges: string;
          submitRequest: string;
        };
        cancel: string;
        comment: string;
        completeBlocked: string;
        confirm: string;
        confirmMessage: string;
        confirmReject: string;
        confirmTitle: string;
        confirmWarning: string;
        submitBlocked: string;
      };
    };
    history: {
      by: string;
      changeTypes: Record<DevelopmentPathChangeTypeDto, string>;
      cycleState: { initial: string; transition: string };
      detailSet: string;
      detailUpdated: string;
      fields: Record<DevelopmentPathStepFieldDto, string>;
      sources: Record<DevelopmentPathChangeSourceDto, string>;
      title: string;
      unnamedStep: string;
    };
    historyDialog: {
      button: string;
      close: string;
      empty: string;
      loadingPreview: string;
      preview: string;
      restore: {
        blocked: {
          ACTIVE_PLAN_EXISTS: string;
          CAMPAIGN_MISMATCH: string;
        };
        button: string;
        error: string;
        success: string;
      };
      stepsCount: string;
      title: string;
    };
    milestone: {
      completeBlockedUntilRealization: string;
      completedOn: string;
      delete: string;
      edit: string;
      markComplete: string;
      overdue: string;
      statusDone: string;
      statusInProgress: string;
      statusPlanned: string;
      step: string;
      undoComplete: string;
      viewDetails: string;
    };
    onboarding: {
      ai: {
        description: string;
        manualAction: string;
        primaryAction: string;
        title: string;
      };
      chatInput: {
        description: string;
        primaryAction: string;
        title: string;
      };
      direction: {
        description: string;
        title: string;
      };
      directionForm: {
        description: string;
        primaryAction: string;
        title: string;
      };
      landing: {
        description: string;
        title: string;
      };
      manual: {
        description: string;
        title: string;
      };
      manualForm: {
        description: string;
        primaryAction: string;
        title: string;
      };
      progress: string;
      viewMode: {
        description: string;
        primaryAction: string;
        title: string;
      };
    };
    pendingSupervisorInitiation: {
      description: string;
      title: string;
    };
    plan: {
      addStep: string;
      addStepForm: {
        actionPlaceholder: string;
        actions: string;
        addAction: string;
        cancel: string;
        deadline: string;
        deadlineWithinCycle: string;
        editTitle: string;
        maxActions: string;
        milestone: string;
        save: string;
        saveError: string;
        startDate: string;
        title: string;
      };
      completedSection: string;
      confirmDelete: string;
      deleteDialog: { cancel: string; confirm: string; title: string };
      draftLabel: string;
      emptyStepsDescription: string;
      inProgressSection: string;
      progress: {
        archived: string;
        archivedEmpty: string;
        cancelled: string;
        cancelledEmpty: string;
        completed: string;
        completedEmpty: string;
        draft: string;
        draftEmpty: string;
        draftHint: string;
        noPlan: string;
        realization: string;
        realizationEmpty: string;
        rejected: string;
        rejectedHint: string;
        stepsCount_few: string;
        stepsCount_many: string;
        stepsCount_one: string;
        stepsCount_other: string;
      };
      progressLabel: string;
      rejectionComment: string;
      reorder: string;
      startWorking: string;
      startWorkingCta: string;
    };
    planBlockedDcFeedback: {
      description: string;
      employeeDescription: string;
      employeeTitle: string;
      title: string;
    };
    preview: {
      error: {
        description: string;
        retry: string;
        title: string;
      };
      returnToActive: string;
      subtitle: string;
      title: string;
    };
    stepsOnboarding: {
      ai: {
        description: string;
        manualAction: string;
        primaryAction: string;
        title: string;
      };
      chatInput: {
        description: string;
        primaryAction: string;
        title: string;
      };
      landing: {
        description: string;
        title: string;
      };
      manual: {
        description: string;
        title: string;
      };
      manualForm: {
        description: string;
        primaryAction: string;
        title: string;
      };
      progress: string;
      viewMode: {
        description: string;
        primaryAction: string;
        title: string;
      };
    };
    supervisorOnboarding: {
      direction: {
        description: string;
        title: string;
      };
      directionForm: {
        description: string;
        primaryAction: string;
        title: string;
      };
      progress: string;
    };
    tabs: {
      activities: string;
      employeeActivities: string;
      plan: string;
    };
    title: string;
    viewMode: {
      chatOnly: string;
      label: string;
      planOnly: string;
      split: string;
    };
  };
  questMap: {
    currentStep: string;
    deadline: string;
    step: string;
  };
  sidebar: {
    backToStart: string;
    collapseAriaLabel: string;
    nav: {
      basicData: string;
      dashboard: string;
      development: string;
      employeeDevelopment: string;
      tasks: string;
    };
    roleSwitcher: {
      ariaLabel: string;
      label: string;
    };
    subNav: {
      activities: string;
      employeeActivities: string;
      planAndAssistant: string;
    };
  };
  tasks: {
    actions: {
      cancel: string;
      done: string;
      start: string;
    };
    completed: string;
    createdAt: string;
    daysAgo: string;
    deadline: string;
    detailsDialog: {
      aboutLabel: string;
      autoCloseIntro: string;
      close: string;
      goToPlan: string;
      reason: {
        DEVELOPMENT_PLAN_INITIATION_REQUIRED: string;
        DEVELOPMENT_PLAN_REQUIRED: string;
        DEVELOPMENT_PLAN_REVIEW_REQUIRED: string;
        DEVELOPMENT_STEP_IN_PROGRESS: string;
        DEVELOPMENT_STEP_OVERDUE: string;
        MANUAL: string;
      };
      start: string;
      title: string;
    };
    empty: string;
    filters: {
      active: string;
      all: string;
      done: string;
      overdue: string;
    };
    openForm: string;
    process: string;
    resourceType: Record<ResourceTypeDto, string>;
    showing: string;
    showMore: string;
    start: string;
    status: Record<'overdue' | TaskStatusDto, string>;
    title: string;
  };
};

export const myProfileTranslation: MyProfileTranslation = {
  activities: {
    count: '{{count}} activities',
    documents: {
      adminSectionTitle: 'Materials from the administrator',
      adminSource: 'From the administrator',
      aiContext: 'AI context',
      aiContextTooltip: 'When on, the chat assistant can use the content of this document.',
      deleteDialog: {
        cancel: 'Cancel',
        confirm: 'Remove document',
        message: 'Are you sure you want to remove this document? This action cannot be undone.',
        title: 'Remove document',
      },
      dropzoneHintPrefix: 'Accepted: PDF, DOCX, XLSX · max.',
      dropzoneLabel: 'Drag files or click to upload',
      dropzoneMaxSize: '20 MB / file',
      embeddingStatus: {
        available: '✓ Available in AI chat',
        enableHint: 'Ready — turn on AI context',
        failed: 'Processing error',
        noContent: 'No content for AI',
        processing: 'Processing…',
        unsupported: 'Unsupported format',
        willBeAvailable: 'Will be available after processing',
      },
      reembed: 'Reprocess',
      title: 'Documents & results',
    },
    empty: 'No activities yet',
    fields: {
      developmentProgram: 'DEVELOPMENT PROGRAM',
      editYear: 'EDITION YEAR',
    },
    noAccess: 'You do not have access to these activities',
    showing: 'Showing {{visible}} of {{total}}',
    showMore: 'Show more',
    status: {
      completed: 'Completed',
      inProgress: 'In progress',
      planned: 'Planned',
    },
    tabs: {
      active: 'Active',
      archived: 'Archived',
    },
  },
  basicInfo: {
    actions: {
      downloadFlashcard: 'Download flashcard',
    },
    elevated: {
      absenceStatus: 'Absence status',
      activeEmployee: 'Active employee in the current organizational structure',
      additionalInfo: 'Additional information',
      age: 'Age',
      aiSummaryDisclaimer:
        'Summary generated automatically by AI. It may contain errors or omissions, or fail to reflect the full picture of the employee\u2019s experience, competencies and potential. The final assessment and decisions rest with the supervisor.',
      aiSummaryFailed: 'We could not prepare the summary. Try again.',
      aiSummaryGenerating: 'We are preparing the summary. It will take a moment.',
      aiSummaryRetry: 'Try again',
      aiSummaryTakingLonger:
        'This is taking longer than usual. Try again or come back in a few minutes.',
      aiSummaryTitle: 'AI-generated employee summary',
      annualEvaluation: 'Annual evaluation result',
      careerPath: 'Career path · Next step',
      company: 'Company',
      countryMobility: 'Domestic mobility',
      developmentContext: 'Development areas',
      education: 'Education',
      englishLevel: 'English level',
      evaluationType: 'Evaluation type',
      expatStatus: 'Expat status',
      feedback: {
        form: {
          cancelButton: 'Cancel',
          description: 'Reason for not providing feedback',
          label: 'Update feedback status',
          sendNotProvidedButton: 'Not provided',
          sendProvidedButton: 'Provided',
          submitNotFeedbackButton: 'Save',
        },
        noReason: 'No reason was provided.',
        notProvided: 'Feedback not provided',
        provided: 'Feedback provided',
      },
      finalGrade: 'Final grade',
      functionalArea: 'Functional area',
      grade: 'Grade',
      internationalMobility: 'International mobility',
      latestProfileData: 'Based on latest profile data',
      leadershipPotential: 'Leadership potential',
      leavingRisk: 'Risk of leaving',
      managementTrainee: 'Management trainee programme',
      mdrResult: 'MDR potential review result',
      no: 'No',
      noComment: 'No feedback available.',
      noEvaluationData: 'No annual evaluation data available.',
      noMdrData: 'No MDR review data available.',
      personalLevel: 'Personal level',
      potential: 'Potential',
      profileDetails: 'Profile details',
      profileSignals: 'Key strengths',
      provided: 'Provided',
      qualitativeGrade: 'Qualitative grade',
      quantitativeGrade: 'Quantitative grade',
      reviewComment: 'Review comment',
      sopAnalysis: 'SOP analysis',
      sopResult: 'SOP result',
      yes: 'Yes',
    },
    employeeSupervisorsLabel: 'EMPLOYEE SUPERVISORS',
    fields: {
      department: 'DEPARTMENT',
      hrBusinessPartner: 'HR BUSINESS PARTNER',
      position: 'POSITION',
      region: 'REGION',
      sapNumber: 'SAP NUMBER',
      tenureJmp: 'SENIORITY IN JMP',
      tenurePosition: 'SENIORITY IN POSITION',
    },
    roles: {
      employeeSelf: 'Employee',
      hrBusinessPartner: 'HR Business Partner',
      p1Manager: 'P1 Manager',
      p2Manager: 'P2 Manager',
      self: 'Me',
    },
    supervisorsLabel: 'MY SUPERVISORS',
    title: 'Basic Info',
  },
  common: {
    noData: 'No data',
  },
  dashboard: {
    activities: {
      continue: 'Continue',
      empty: 'No development activities',
      more: '+ {{count}} more activities',
      open: 'Open',
      title: 'Development activities',
    },
    currentSteps: {
      daysAgo: '({{count}} days ago)',
      empty: 'No active tasks',
      moreTasks: '+ {{count}} more tasks',
      title: 'Current tasks',
    },
    directionLabel: 'MY DEVELOPMENT DIRECTION',
    employeeDirectionLabel: 'EMPLOYEE DEVELOPMENT DIRECTION',
    emptyPlan: {
      action: 'Create plan',
      description: 'Create a development plan to track progress, steps, and AI-supported work.',
      title: 'No development plan yet',
    },
    goToDevelopment: 'Go to “My Development”',
    goToEmployeeDevelopment: 'Go to “Employee development”',
    noDirection: 'No development plan assigned',
    roleView: {
      activitiesInProgress: 'Activities in progress',
      completedSteps: '{{completed}} of {{total}} steps completed',
      emptyPlan: {
        description: 'This employee has not started a development plan yet.',
        title: 'No development plan',
      },
      giveFeedback: 'Provide feedback',
      goToPlan: 'Go to plan',
      noOverdue: 'No overdue steps',
      overdueDays: '{{count}} days overdue',
      overdueSteps: 'Overdue step',
      planDirection: 'Development direction: {{direction}}',
      planProgress: 'Plan progress',
    },
    stats: {
      activitiesInProgress: 'Activities in progress',
      overdueTasks: 'Overdue tasks',
      stepsCompleted: 'Steps completed',
    },
    title: 'Development Panel',
  },
  development: {
    aiAssistant: {
      aiNotice: {
        intro:
          'jAIn is an artificial-intelligence tool that supports your learning, development and career planning. The more information you share about yourself, the better tailored the recommendations will be.',
        points: {
          errors: 'AI answers may be incorrect or incomplete.',
          otherPeopleData:
            'Do not provide other people\u2019s data unless your job duties require it and you have the appropriate permissions.',
          security: 'Use the tool in line with data protection and information security rules.',
        },
        rememberLabel: 'Remember:',
      },
      casualGreeting:
        'Ask me questions about your skills or choose from the most common ones below.',
      casualMode: 'Casual chat',
      casualModeHeader: 'MODE: CASUAL CHAT',
      casualSuggestions: {
        conflictResolution: 'Conflict Resolution',
        feedback: 'Feedback',
        keyCompetencies: 'Key Competencies',
        matchProjects: 'Match Projects',
        taskDelegation: 'Task Delegation',
      },
      employeeModeHeader:
        'MODE: CHAT ABOUT EMPLOYEE – LEARN MORE ABOUT YOUR EMPLOYEE AND THEIR SKILLS',
      noActivePlan: 'No active plan',
      noActivePlanDescription:
        'The assistant will be available after creating an active development plan.',
      planGreeting: 'Hi, write to me so we can build your development plan together.',
      planMode: 'Work with plan',
      planModeHeader: 'MODE: WORK WITH DEVELOPMENT PLAN',
      planSuggestions: {
        addStep: 'Add step',
        nextStep: 'Next step',
        summarize: 'Summarise progress',
        update: 'Update plan',
      },
      selectMode: 'Choose assistant mode',
      title: 'AI Development Assistant',
    },
    campaignInactive: {
      description:
        'You are not covered by any development cycle at the moment, so you cannot create a plan. Plan creation unlocks once a cycle covers you.',
      employeeDescription:
        'This employee is not covered by any development cycle, so a plan cannot be created for them. Plan creation unlocks once a cycle covers them.',
      employeeTitle: 'Employee is not covered by a development cycle',
      title: 'You are not covered by a development cycle',
    },
    certificationResults: {
      description: 'You can add steps to your plan based on your certification results.',
      link: 'Check your results',
    },
    employeeTitle: 'Employee Development',
    emptyPlan: {
      chatReadyDescription:
        'The plan workspace is ready. Work with the AI assistant to prepare development steps.',
      create: 'Define development direction',
      creating: 'Creating...',
      description:
        'There is no development plan for this employee yet. Create one to define the direction and start working with the AI assistant.',
      direction: 'Development direction',
      directionFromCampaign:
        'The direction was set by the administrator in the development campaign and cannot be changed.',
      initialize: 'Start plan',
      noPermission: 'You do not have permission to create a development plan for this profile.',
      submit: 'Start',
      supervisorDescription:
        "This employee doesn't have a development plan yet. Set the direction and start date to initiate it.",
      title: 'No development plan yet',
      validFrom: 'Valid from',
      validTo: 'Valid to',
    },
    goalBar: {
      approver: 'Approver: {{name}} (P1)',
      cancel: 'Cancel',
      cycle: 'Cycle:',
      directionFromCampaign:
        'The direction was set by an administrator in a development campaign and cannot be changed.',
      directionLabel: 'Development plan direction:',
      save: 'Save',
      statusChange: {
        approval: {
          approve: 'Approve plan',
          awaitingMessage: 'The employee is awaiting acceptance of the plan update.',
          cancel: 'Cancel',
          commentLabel: 'Comment for the employee',
          commentPlaceholder: 'Describe what needs to be improved in this plan...',
          requestChanges: 'Request changes',
          submitRequest: 'Send change request',
        },
        cancel: 'Cancel',
        comment: 'Comment',
        completeBlocked: 'All milestones must be completed first',
        confirm: 'Confirm',
        confirmMessage: 'Are you sure you want to {{action}} this cycle?',
        confirmReject: 'Confirm rejection',
        confirmTitle: 'Irreversible action',
        confirmWarning:
          'After confirmation the cycle becomes inactive and moves to historical cycles — it can no longer be edited or reverted.',
        submitBlocked: 'At least 2 steps are required',
      },
    },
    history: {
      by: 'By:',
      changeTypes: {
        [DevelopmentPathChangeTypeDto.CycleStateChange]: 'Cycle status changed',
        [DevelopmentPathChangeTypeDto.StepAdded]: 'Step added \u201c{{milestone}}\u201d',
        [DevelopmentPathChangeTypeDto.StepDeleted]: 'Step removed \u201c{{milestone}}\u201d',
        [DevelopmentPathChangeTypeDto.StepMarkedDone]:
          'Step marked complete \u201c{{milestone}}\u201d',
        [DevelopmentPathChangeTypeDto.StepMarkedUndone]:
          'Step completion undone \u201c{{milestone}}\u201d',
        [DevelopmentPathChangeTypeDto.StepUpdated]: 'Step changed \u201c{{milestone}}\u201d',
      },
      cycleState: {
        initial: 'Plan draft created',
        transition: 'Plan status: {{from}} \u2192 {{to}}',
      },
      detailSet: '{{field}}: {{to}}',
      detailUpdated: '{{field}}: {{from}} \u2192 {{to}}',
      fields: {
        [DevelopmentPathStepFieldDto.Actions]: 'actions',
        [DevelopmentPathStepFieldDto.Deadline]: 'deadline',
        [DevelopmentPathStepFieldDto.Milestone]: 'name',
        [DevelopmentPathStepFieldDto.StartDate]: 'start',
      },
      sources: {
        [DevelopmentPathChangeSourceDto.AiChat]: 'via AI chat',
        [DevelopmentPathChangeSourceDto.Manual]: 'manually',
        [DevelopmentPathChangeSourceDto.System]: 'automatically',
      },
      title: 'History',
      unnamedStep: 'unnamed step',
    },
    historyDialog: {
      button: 'Plan history',
      close: 'Close',
      empty: 'No historical development plans.',
      loadingPreview: 'Loading historical plan...',
      preview: 'Preview',
      restore: {
        blocked: {
          ACTIVE_PLAN_EXISTS: 'Finish the active development plan to restore this one.',
          CAMPAIGN_MISMATCH:
            'The plan comes from a different development cycle than the current one.',
        },
        button: 'Restore',
        error: 'Could not restore the plan.',
        success: 'The plan has been restored.',
      },
      stepsCount: '{{completed}}/{{total}} steps',
      title: 'Historical development plans',
    },
    milestone: {
      completeBlockedUntilRealization:
        'Set the plan status to "In progress" before marking steps as complete',
      completedOn: 'Completed: {{date}}',
      delete: 'Delete step',
      edit: 'Edit step',
      markComplete: 'Mark as complete',
      overdue: 'Overdue ({{days}} days)',
      statusDone: 'Completed',
      statusInProgress: 'In progress',
      statusPlanned: 'Planned',
      step: 'Step {{index}}:',
      undoComplete: 'Undo finish',
      viewDetails: 'Show details',
    },
    onboarding: {
      ai: {
        description:
          'Let the AI assistant build your steps with you, or add them yourself. You can switch anytime.',
        manualAction: "I'll do it myself",
        primaryAction: 'Work with plan',
        title: 'How would you like to add steps?',
      },
      chatInput: {
        description:
          "Tell the assistant your goal, or tap a suggestion. It'll turn your chat into ready-made steps.",
        primaryAction: 'Got it',
        title: 'Just start chatting',
      },
      direction: {
        description:
          'Before adding any steps, tell us what you want to work on and the dates your plan will cover.',
        title: 'Start with your goal',
      },
      directionForm: {
        description:
          "Write down what you want to grow toward and pick a start and end date. We'll set up your plan right after.",
        primaryAction: 'Fill in the form',
        title: 'Fill in your goal and dates',
      },
      landing: {
        description:
          "We'll guide you through it, step by step. First set your goal and dates — then add what you want to work on, with AI or on your own.",
        title: "Let's build your development plan",
      },
      manual: {
        description:
          'Use "Add new step" to set a milestone, its dates, and the actions you\'ll take — no assistant needed.',
        title: 'Add your steps yourself',
      },
      manualForm: {
        description:
          "Name your milestone, set the dates, and list the actions that'll get you there.",
        primaryAction: 'Got it',
        title: 'Add your first step',
      },
      progress: '{{current}} of {{total}}',
      viewMode: {
        description:
          'See your plan and the chat side by side, or focus on just one — whatever feels comfortable.',
        primaryAction: 'Show me',
        title: "Pick how you'd like to work",
      },
    },
    pendingSupervisorInitiation: {
      description:
        'Your supervisor has been asked to initiate your development plan (set the direction and dates). Once they do, your plan will appear here. If they do not act within 7 days, you will be able to create the plan yourself.',
      title: 'Your supervisor is initiating your development plan',
    },
    plan: {
      addStep: 'Add new step',
      addStepForm: {
        actionPlaceholder: 'Describe the action…',
        actions: 'ACTIONS',
        addAction: 'Add action',
        cancel: 'Cancel',
        deadline: 'Deadline',
        deadlineWithinCycle: 'The step deadline cannot go past the end of the cycle ({{to}}).',
        editTitle: 'Edit step',
        maxActions: 'A step can have at most {{max}} actions.',
        milestone: 'Milestone',
        save: 'Save step',
        saveError: 'Could not save the step.',
        startDate: 'Start date',
        title: 'New step',
      },
      completedSection: 'COMPLETED STEPS',
      confirmDelete: 'Are you sure you want to delete this plan step?',
      deleteDialog: {
        cancel: 'Cancel',
        confirm: 'Delete step',
        title: 'Delete plan step',
      },
      draftLabel: 'Draft',
      emptyStepsDescription: 'Add the first step to start tracking progress on this plan.',
      inProgressSection: 'IN PROGRESS & PLANNED',
      progress: {
        archived: 'Plan archived ({{completed}} of {{total}} steps completed)',
        archivedEmpty: 'Plan archived (no steps)',
        cancelled: 'Plan cancelled ({{completed}} of {{total}} steps completed)',
        cancelledEmpty: 'Plan cancelled (no steps)',
        completed: 'Plan completed ({{completed}} of {{total}} steps)',
        completedEmpty: 'Plan completed (no steps)',
        draft: 'Draft ({{steps}})',
        draftEmpty: 'Draft (no steps yet)',
        draftHint: 'The plan needs at least {{min}} steps before it can be sent for approval.',
        noPlan: 'No development plan',
        realization: 'In progress ({{completed}} of {{total}} steps)',
        realizationEmpty: 'Start working on the plan',
        rejected: 'Returned for changes ({{steps}})',
        rejectedHint: 'Update the plan and send it for approval again.',
        stepsCount_few: '{{count}} steps',
        stepsCount_many: '{{count}} steps',
        stepsCount_one: '{{count}} step',
        stepsCount_other: '{{count}} steps',
      },
      progressLabel: 'Plan progress:',
      rejectionComment: 'Supervisor comment:',
      reorder: 'Reorder',
      startWorking: 'Start working on the plan',
      startWorkingCta: 'Talk to the AI Development Assistant to add development steps',
    },
    planBlockedDcFeedback: {
      description:
        'Creating a plan opens up once your feedback meeting after the Development Center session is noted down. Your manager or HR will arrange it.',
      employeeDescription:
        'Creating a plan opens up once the feedback meeting after the Development Center session is noted down. Arrange the meeting or check with HR.',
      employeeTitle: 'Employee is waiting for the feedback meeting after the Development Center',
      title: 'Waiting for the feedback meeting after the Development Center',
    },
    preview: {
      error: {
        description: 'Try again or go back to the current plan.',
        retry: 'Try again',
        title: 'Could not open the plan',
      },
      returnToActive: 'Return to current plan',
      subtitle: '{{direction}} ({{validFrom}} - {{validTo}})',
      title: 'Preview mode: historical plan',
    },
    stepsOnboarding: {
      ai: {
        description:
          'Let the AI assistant build your steps with you, or add them yourself. You can switch anytime.',
        manualAction: "I'll do it myself",
        primaryAction: 'Work with plan',
        title: 'How would you like to add steps?',
      },
      chatInput: {
        description:
          "Tell the assistant your goal, or tap a suggestion. It'll turn your chat into ready-made steps.",
        primaryAction: 'Got it',
        title: 'Just start chatting',
      },
      landing: {
        description:
          'Your supervisor already set the direction and dates. Click here to add the steps that will get you there.',
        title: 'Your plan is waiting for its steps',
      },
      manual: {
        description:
          'Use "Add new step" to set a milestone, its dates, and the actions you\'ll take — no assistant needed.',
        title: 'Add your steps yourself',
      },
      manualForm: {
        description:
          "Name your milestone, set the dates, and list the actions that'll get you there.",
        primaryAction: 'Got it',
        title: 'Add your first step',
      },
      progress: '{{current}} of {{total}}',
      viewMode: {
        description:
          'See your plan and the chat side by side, or focus on just one — you can switch anytime.',
        primaryAction: 'Show me',
        title: 'Your plan is ready — add the steps',
      },
    },
    supervisorOnboarding: {
      direction: {
        description:
          'This employee is waiting for their development plan to be initiated. Click here to set the direction and start date.',
        title: "Initiate your employee's plan",
      },
      directionForm: {
        description:
          "Enter the employee's development direction and pick a start date. They'll fill in the rest of the plan themselves.",
        primaryAction: 'Fill in the form',
        title: 'Add the goal and date',
      },
      progress: '{{current}} of {{total}}',
    },
    tabs: {
      activities: 'My Activities',
      employeeActivities: 'Activities',
      plan: 'Development Plan & AI Assistant',
    },
    title: 'My Development',
    viewMode: {
      chatOnly: 'Chat-only view',
      label: 'CHANGE VIEW',
      planOnly: 'Plan-only view',
      split: 'Default view',
    },
  },
  questMap: {
    currentStep: 'Current step',
    deadline: 'Deadline: {{date}}',
    step: 'Step {{number}}',
  },
  sidebar: {
    backToStart: 'Back to Start',
    collapseAriaLabel: 'Collapse profile menu',
    nav: {
      basicData: 'Basic Info',
      dashboard: 'Development Panel',
      development: 'My Development',
      employeeDevelopment: 'Employee development',
      tasks: 'My Tasks',
    },
    roleSwitcher: {
      ariaLabel: 'Profile role switcher',
      label: 'Viewing as',
    },
    subNav: {
      activities: 'My Activities',
      employeeActivities: 'Activities',
      planAndAssistant: 'Development Plan & AI Assistant',
    },
  },
  tasks: {
    actions: {
      cancel: 'Cancel',
      done: 'Done',
      start: 'Start',
    },
    completed: 'Completed',
    createdAt: 'Created: {{date}}',
    daysAgo: '({{count}} days ago)',
    deadline: 'Deadline: {{date}}',
    detailsDialog: {
      aboutLabel: 'What it is about',
      autoCloseIntro: 'This task will close automatically once you:',
      close: 'Close',
      goToPlan: 'Go to the development plan',
      reason: {
        DEVELOPMENT_PLAN_INITIATION_REQUIRED:
          'create a development plan for this subordinate (you have 7 days — afterwards the employee creates it themselves)',
        DEVELOPMENT_PLAN_REQUIRED: 'create and submit a development plan for approval',
        DEVELOPMENT_PLAN_REVIEW_REQUIRED: "approve or reject your subordinate's development plan",
        DEVELOPMENT_STEP_IN_PROGRESS: 'mark this development-plan step as completed',
        DEVELOPMENT_STEP_OVERDUE: 'mark this overdue step as completed',
        MANUAL: 'complete the required action',
      },
      start: 'Start',
      title: 'Task details',
    },
    empty: 'No tasks available',
    filters: {
      active: 'In progress',
      all: 'All',
      done: 'Completed',
      overdue: 'Overdue',
    },
    openForm: 'Open form',
    process: '• Review process',
    resourceType: {
      [ResourceTypeDto.App]: 'App',
      [ResourceTypeDto.DevelopmentPath]: 'Development path',
      [ResourceTypeDto.Employee]: 'Employee',
      [ResourceTypeDto.Survey]: 'Survey',
    },
    showing: 'Showing {{visible}} of {{total}}',
    showMore: 'Show more',
    start: 'Start',
    status: {
      overdue: 'Overdue',
      [TaskStatusDto.Cancelled]: 'Cancelled',
      [TaskStatusDto.Done]: 'Completed',
      [TaskStatusDto.InProgress]: 'In progress',
      [TaskStatusDto.Open]: 'New',
    },
    title: 'Current development steps',
  },
};
